import type { SidebarFontSchemaType, SidebarSchemaType } from '@vedalib/editor/lib/brand'
import type { CourseStructureItem } from '@vedalib/editor/lib/elements'
import lodash from 'lodash'
import type { CSSProperties } from 'react'

import type { ISectionTreeNode } from 'components/sections/ProjectStructure/utils'
import type { KeyIconsType } from 'components/uiKit/Icon'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import type { BrandType, ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { BlockMode, SectionTypeEnum } from 'services/Store/Project/enums'
import type { Section } from 'services/Store/Project/types'

const FONTS_SECTION = ['color', 'fontFamily', 'fontWeight', 'fontStyle', 'fontSize', 'lineHeight']

type SectionConfigType = {
  [key in SectionTypeEnum]: {
    icon: KeyIconsType
    getName: (pt: (key: keyof BrandType['labels']) => string) => string
    getImage: (styles: ElementStyleCss<SidebarSchemaType>) => string | null | undefined
    getShowIcon: (styles: ElementStyleCss<SidebarSchemaType>) => boolean
    countDetails: (
      item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      childCount?: number,
    ) => string
    getFonts: (font: ElementFontCss<SidebarFontSchemaType>) => Partial<CSSProperties>
  }
}

export const sectionConfig: SectionConfigType = {
  [SectionTypeEnum.landing]: {
    icon: 'contentsLanding' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.landing'),
    getImage: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.sectionIcons.backgroundImage,
    getShowIcon: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.sectionIcons.showIcons,
    countDetails: (_item: CourseStructureItem, pt: (key: keyof BrandType['labels']) => string) =>
      pt('courseStructure.sections'),
    getFonts: (font: ElementFontCss<SidebarFontSchemaType>) =>
      lodash.pick(font?.section, FONTS_SECTION),
  },
  [SectionTypeEnum.test]: {
    icon: 'contentsTest' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.test'),
    getImage: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.testIcons.backgroundImage,
    getShowIcon: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.testIcons.showIcons,
    countDetails: (item: CourseStructureItem, pt: (key: keyof BrandType['labels']) => string) =>
      `${item.blocks.filter(({ mode }) => mode === BlockMode.questions).length} ${pt('courseStructure.questions')}`,
    getFonts: (font: ElementFontCss<SidebarFontSchemaType>) =>
      lodash.pick(font?.section, FONTS_SECTION),
  },
  [SectionTypeEnum.chapter]: {
    icon: 'contentsChapter' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.chapter'),
    getImage: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.chapterIcons.backgroundImage,
    getShowIcon: (styles: ElementStyleCss<SidebarSchemaType>) => styles?.chapterIcons.showIcons,
    countDetails: (
      _item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      cildCount?: number,
    ) => `${cildCount || 0} ${pt('courseStructure.sections')}`,
    getFonts: (font: ElementFontCss<SidebarFontSchemaType>) =>
      lodash.pick(font?.chapter, FONTS_SECTION),
  },
  [SectionTypeEnum.cover]: {
    icon: 'contentsSlides' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.cover'),
    getImage: (_styles: ElementStyleCss<SidebarSchemaType>) => null,
    getShowIcon: (_styles: ElementStyleCss<SidebarSchemaType>) => false,
    countDetails: (_item: CourseStructureItem, pt: (key: keyof BrandType['labels']) => string) =>
      pt('courseStructure.sections'),
    getFonts: (_font: ElementFontCss<SidebarFontSchemaType>) => EMPTY_OBJECT,
  },
}

export const getIconData = ({
  type,
  styles,
  font,
  nestedOffset,
  disabled,
}: {
  type: SectionTypeEnum
  styles: ElementStyleCss<SidebarSchemaType>
  font: ElementFontCss<SidebarFontSchemaType>
  nestedOffset?: number
  disabled?: boolean
}): {
  showIcon: boolean
  IconImage: string | null | undefined
  styleFontSection: React.CSSProperties
  icon: KeyIconsType
  sectionNameStyles: React.CSSProperties
  iconStyles: React.CSSProperties
} => {
  const { getShowIcon, getImage, getFonts, icon } = sectionConfig[type]

  const showIcon = getShowIcon(styles)
  const IconImage = getImage(styles)
  const fontSection = getFonts(font)
  const styleFontSection = {
    ...fontSection,
    ...(disabled && { color: styles.item_disable.color }),
  }

  const iconStyles = {
    ...(IconImage && { backgroundImage: IconImage }),
    marginRight: styles?.margins.offsetFromIcon,
    marginLeft: nestedOffset,
  }

  const sectionNameStyles = { ...styles?.root, ...(!showIcon && { marginLeft: nestedOffset }) }

  return { showIcon, IconImage, styleFontSection, icon, sectionNameStyles, iconStyles }
}

export const getSectionHasFirstBlock = (nodes: ISectionTreeNode<Section>[]) => {
  return nodes?.find(({ item }) => {
    if (!item.isChapter) {
      const { blocks = [], blocksOrder = [] } = item || {}
      const blockNumbers = blocks
        .filter(({ isHide }) => !isHide)
        .map(({ uuid }) => blocksOrder.findIndex((id) => uuid === id))?.[0]
      const firstBlockId = blocksOrder[blockNumbers]
      if (firstBlockId) {
        return item
      }
    }
    return undefined
  })?.item
}

export const parseStyleValue = (value?: string | number) => {
  if (typeof value === 'string') {
    return parseFloat(value) || 0
  }

  return value || 0
}
